import { useQueries, useQuery } from '@tanstack/react-query'
import { request, requestList, orgBase } from '@/api/http'
import type * as playbook from '@/api/types/playbook'

// ── qtz-readiness /playbook/* ───────────────────────────────────────────────
// qtz-readiness keeps its own copy of qtz-playbook's Issuing CA register and
// serves it here, with the NIST remediation report it computes over the crypto
// assets it pulled from Discovery; the portal never calls qtz-playbook itself.
// POST /sync asks the backend to pull the register now: 202 with the run
// (status `running` unless `wait` is set), 409 SYNC_IN_PROGRESS while a pull
// is already running, 503 PLAYBOOK_NOT_CONFIGURED when the service has no
// Playbook URL (the routes then serve whatever the last sync or the dev seed
// left). GET /remediation answers 503 REMEDIATION_UNAVAILABLE when the
// backend's embedded NIST table failed to load; fetchPlaybookFacts treats that
// as an empty report rather than an error.

const base = (orgId: string) => `${orgBase(orgId)}/playbook`

export const playbookApi = {
  summary(orgId: string): Promise<playbook.Summary> {
    return request(`${base(orgId)}/summary`)
  },
  issuingCas(orgId: string): Promise<playbook.CARegister> {
    return request(`${base(orgId)}/issuing-cas`)
  },
  remediation(orgId: string): Promise<playbook.RemediationReport> {
    return request(`${base(orgId)}/remediation`)
  },
  objectivePlans(orgId: string, code: string): Promise<playbook.ObjectivePlans> {
    return request(`${base(orgId)}/objectives/${encodeURIComponent(code)}/plans`)
  },
  syncRuns(orgId: string): Promise<playbook.SyncRun[]> {
    return requestList(`${base(orgId)}/sync-runs`)
  },
  syncRun(orgId: string, runId: number): Promise<playbook.SyncRun> {
    return request(`${base(orgId)}/sync-runs/${runId}`)
  },
  sync(orgId: string, body: playbook.SyncRequest = {}): Promise<playbook.SyncRun> {
    return request(`${base(orgId)}/sync`, { method: 'POST', body: JSON.stringify(body) })
  },
}

const plansQuery = (orgId: string, code: string) => ({
  queryKey: ['playbook-plans', orgId, code],
  queryFn: () => playbookApi.objectivePlans(orgId, code),
  retry: false,
})

// One objective's Playbook plans and vendor gating, while a drawer shows a
// cell the readiness service serves (code null otherwise).
export function useObjectivePlans(orgId: string, code: string | null) {
  return useQuery({ ...plansQuery(orgId, code ?? ''), enabled: !!orgId && !!code })
}

// Several objectives' plans, keyed by code, for the cards view.
export function useObjectivesPlans(orgId: string, codes: string[]): Map<string, playbook.ObjectivePlans> {
  return useQueries({
    queries: codes.map((code) => ({ ...plansQuery(orgId, code), enabled: !!orgId })),
    combine: (results) => new Map(results.flatMap((r, i) => (r.data ? [[codes[i], r.data] as const] : []))),
  })
}
